import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  withStyles,
  MenuItem,
} from '@material-ui/core';
import { Redirect } from 'react-router';
import CustomTextField from '../../../components/Inputs/TextField/CustomTextField';
import CustomAutocomplete from '../../../components/Inputs/Autocomplete/CustomAutocomplete';
import useStyles from './styles';
import storeRequest from '../../../requests/api/store';
import { SUPER_ADMIN } from '../../../utils/const/user_types';
import { COUNTRIES, CHILE } from '../../../utils/const/countries';
import { REGIONS, METROPOLITANA } from '../../../utils/const/regions';
import { getCommunesByRegion } from '../../../utils/const/communes';
import handleApiResponse from '../../../utils/handleApiResponse';
import { setStoreAllUsersCheck, setStoreCreationPage, setStoreDetails } from '../../../actions/storeActions';

function Results({
  className, clients, users, setSelectedClientId, priceLists, setSearchValue, account, ...rest
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [localPriceList, setLocalPriceList] = useState(null);
  const [discountLocalPriceList, setDiscountLocalPriceList] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const { storeAllUsersCheck, storeInformation } = useSelector((state) => state.store);

  const DefaultCheckbox = withStyles((theme) => ({
    root: {
      color: theme.palette.primary.main,
      '&$checked': {
        color: theme.palette.primary.main,
      },
    },
    checked: {},
  }))((props) => <Checkbox color="default" {...props} />);

  useEffect(() => {
    if (storeInformation.priceListId && storeInformation.priceListName) {
      setLocalPriceList({ id: storeInformation.priceListId, name: storeInformation.priceListName });
    }
    if (storeInformation.discountPriceListId && storeInformation.discountPriceListName) {
      setDiscountLocalPriceList({
        id: storeInformation.discountPriceListId,
        name: storeInformation.discountPriceListName,
      });
    }
  }, []);

  if (submitSuccess) {
    return <Redirect to="/administracion/tiendas" />;
  }

  return (
    <Formik
      initialValues={{
        name: storeInformation.name || '',
        country: storeInformation.country || CHILE,
        region: storeInformation.region || METROPOLITANA,
        commune: storeInformation.commune || '',
        address: storeInformation.address || '',
        priceListId: storeInformation.priceListId || '',
        priceListName: storeInformation.priceListName || '',
        discountPriceListId: storeInformation.discountPriceListId || '',
        discountPriceListName: storeInformation.discountPriceListName || '',
        clientId: account.user.role === SUPER_ADMIN ? null : account.user.clientId,
      }}
      validationSchema={Yup.object().shape({
        name: Yup.string().max(255).required('Debe ingresar un nombre'),
        country: Yup.string().max(255).required('Debe ingresar el país'),
        region: Yup.string().max(255).required('Debe ingresar la región'),
        commune: Yup.string().max(255).required('Debe ingresar la comuna'),
        address: Yup.string().max(255).required('Debe ingresar una dirección'),
        priceListId: Yup.string().max(255).required('Debe seleccionar una lista de precios'),
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        const sharedData = {
          name: values.name,
          country: values.country,
          region: values.region,
          commune: values.commune,
          address: values.address,
          priceListId: values.priceListId,
          discountPriceListId: values.discountPriceListId,
          clientId: values.clientId,
        };

        if (storeAllUsersCheck) {
          try {
            const data = {
              ...sharedData,
              userIds: users.map((item) => item.id),
            };

            const response = await storeRequest.postCreateStore(data);

            handleApiResponse(enqueueSnackbar, response, true);
            setStatus({ success: true });
            setSubmitting(false);
            setSubmitSuccess(true);
          } catch (error) {
            handleApiResponse(enqueueSnackbar, error, false, 'error', <Button href="/administracion/tiendas">Volver a Mis Locales</Button>);
            setStatus({ success: false });
            setErrors({ submit: error.message });
            setSubmitting(false);
          }
        } else {
          setSearchValue('');
          dispatch(setStoreDetails({
            ...sharedData,
            priceListName: values.priceListName,
            discountPriceListName: values.discountPriceListName,
          }));
          dispatch(setStoreCreationPage(1));
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        setFieldValue,
        values,
      }) => (
        <form
          className={clsx(classes.emptyRoot, className)}
          onSubmit={handleSubmit}
          {...rest}
        >
          <Card>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item md={6} xs={12}>
                  <CustomTextField
                    error={Boolean(touched.name && errors.name)}
                    fullWidth
                    helperText={touched.name && errors.name}
                    label="Nombre"
                    name="name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    value={values.name}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <CustomTextField
                    error={Boolean(touched.country && errors.country)}
                    fullWidth
                    helperText={touched.country && errors.country}
                    label="País"
                    name="country"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    select
                    disabled
                    value={values.country}
                    variant="outlined"
                  >
                    {COUNTRIES.map((country) => (
                      <MenuItem
                        key={country}
                        value={country}
                        selected={country === CHILE}
                      >
                        {country}
                      </MenuItem>
                    ))}
                  </CustomTextField>
                </Grid>
                <Grid item md={6} xs={12}>
                  <CustomTextField
                    error={Boolean(touched.region && errors.region)}
                    fullWidth
                    helperText={touched.region && errors.region}
                    label="Región"
                    name="region"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    select
                    value={values.region}
                    variant="outlined"
                  >
                    {REGIONS.map((region) => (
                      <MenuItem
                        key={region}
                        value={region}
                        selected={region === METROPOLITANA}
                      >
                        {region}
                      </MenuItem>
                    ))}
                  </CustomTextField>
                </Grid>
                <Grid item md={6} xs={12}>
                  <CustomTextField
                    error={Boolean(touched.commune && errors.commune)}
                    fullWidth
                    helperText={touched.commune && errors.commune}
                    label="Comuna"
                    name="commune"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    select
                    value={values.commune}
                    variant="outlined"
                  >
                    {getCommunesByRegion(values.region).map((commune) => (
                      <MenuItem
                        key={commune}
                        value={commune}
                      >
                        {commune}
                      </MenuItem>
                    ))}
                  </CustomTextField>
                </Grid>
                <Grid item md={12} xs={12}>
                  <CustomTextField
                    error={Boolean(touched.address && errors.address)}
                    fullWidth
                    helperText={touched.address && errors.address}
                    label="Dirección"
                    name="address"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    value={values.address}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <CustomAutocomplete
                    disablePortal
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    id="combo-box-demo"
                    fullWidth
                    options={priceLists}
                    value={localPriceList}
                    getOptionLabel={(option) => `${option.name}`}
                    onChange={(e, value) => {
                      if (value === null) return;
                      setFieldValue('priceListId', value.id);
                      setFieldValue('priceListName', value.name);
                      setLocalPriceList(value);
                    }}
                    renderInput={(params) => (
                      <CustomTextField
                        {...params}
                        label="Lista Precios"
                        variant="outlined"
                        required
                      />
                    )}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <CustomAutocomplete
                    disablePortal
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    id="combo-box-demo"
                    fullWidth
                    options={priceLists}
                    getOptionLabel={(option) => `${option.name}`}
                    value={discountLocalPriceList}
                    onChange={(e, value) => {
                      if (value === null) return;
                      setFieldValue('discountPriceListId', value.id);
                      setFieldValue('discountPriceListName', value.name);
                      setDiscountLocalPriceList(value);
                    }}
                    renderInput={(params) => (
                      <CustomTextField
                        {...params}
                        label="Lista Precios Descuento"
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>
                {account.user.role === SUPER_ADMIN
                  && <Grid item md={6} xs={12}>
                    <CustomAutocomplete
                      disablePortal
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      id="combo-box-demo"
                      fullWidth
                      filterSelectedOptions
                      options={clients}
                      getOptionLabel={(option) => `${option.name}`}
                      onChange={(e, value) => {
                        if (value === null) return;
                        setFieldValue('clientId', value.id);
                        setSelectedClientId(value.id);
                      }}
                      renderInput={(params) => (
                        <CustomTextField
                          {...params}
                          label="Buscar cliente"
                          variant="outlined"
                        />
                      )}
                    />
                  </Grid>
                }
              </Grid>
              <Box
                display='flex'
                mt={2}
                justifyContent='space-between'
              >
                <Box
                  display='flex'
                > <FormGroup row>
                    <FormControlLabel
                      control={<DefaultCheckbox checked={storeAllUsersCheck} onChange={(e) => dispatch(setStoreAllUsersCheck(e.target.checked))} name="allUsersbox" />}
                      label="Asociar todos los usuarios"
                    />
                  </FormGroup>
                </Box>
                <Box display='flex' justifyContent='flex-end'>

                  {storeAllUsersCheck
                    ? (
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        className={classes.orangeButton}
                        disabled={isSubmitting}
                      >
                        Crear Local
                      </Button>
                    )
                    : (
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        className={classes.blueButton}
                        disabled={isSubmitting}
                      >
                        Siguiente
                      </Button>
                    )}

                </Box>
              </Box>
            </CardContent>
          </Card>
        </form>
      )}
    </Formik>
  );
}

Results.propTypes = {
  className: PropTypes.string,
};

export default Results;
